import clsx from 'clsx';
import { memo, useMemo } from 'react';

import PlayerIdentifier from '@shared/Identifier/PlayerIdentifier';
import { useTranslateFn } from '@shared/Translate';
import type { Statistic, StatisticDefinition } from '@shared-types/stats.types';
import { getStatNumericValue, getStatValue } from '@utilities/stats';

import * as styles from './styles.module.scss';

export const VerticalTableComparison = ({
  stats,
  statDefs,
  seasons,
  isStory,
}: {
  stats: Statistic[];
  statDefs: StatisticDefinition[];
  seasons: number[];
  isStory?: boolean;
}) => {
  const t = useTranslateFn();

  const types = useMemo(
    () => statDefs?.filter((t) => stats?.some((s) => getStatNumericValue(s, t) != null)),
    [statDefs, stats],
  );

  const getHigherStat = (def: StatisticDefinition, stat: Statistic) => {
    const maxValue = Math.max(...stats.map((s) => getStatNumericValue(s, def)));
    return getStatNumericValue(stat, def) >= maxValue;
  };

  const getLinkActive = (index: number) => {
    const season = seasons?.[index];
    return !!season && season >= window.currentSeason;
  };

  if (!stats?.length || !types?.length) return null;

  return (
    <div pk-theme={isStory ? 'dark' : 'light'} className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('players_to_watch')}</div>
      </div>
      <div className={clsx(styles.playerHeader, styles[`items-${stats.length}`])}>
        {stats?.map((stat, i) => (
          <PlayerIdentifier
            className={clsx(styles.playerIdentifier, 'pk-py--xs')}
            key={`stat_row_${stat.player.id}`}
            player={stat.player}
            team={stat.team}
            useFullName={false}
            showPosition={false}
            showTeamName={false}
            enableLink={getLinkActive(i)}
            type='vertical'
          />
        ))}
      </div>
      {types.map((t) => (
        <div className={styles.statsContainer} key={`type_${t.name}`}>
          <div className={styles.statsContainerTitle} title={t.displayName}>
            {t.displayName}
          </div>
          <div className={styles.statsRow}>
            {stats?.map((stat) => (
              <div
                className={clsx({
                  [styles.cell]: true,
                  [styles.cellHigher]: getHigherStat(t, stat),
                })}
                key={`cell_${t.name}_${stat.playerId}`}
              >
                {getStatValue(stat, t)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
export default memo(VerticalTableComparison);
